import { employeesApiBaseUrl, payrollCalculationApiBaseUrl, payrollConfigApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { PeopleGateway } from './PeopleGateway';
import type {
  ContractorListPage,
  ContractorResponse,
  CreateContractorInput,
  CompanyWorkLocationOption,
  EmployeeInvitationState,
  EmployeeQuestionItem,
  CreateEmployeeInput,
  CreateDepartmentInput,
  DepartmentCodeSuggestion,
  DepartmentOption,
  EmployeeAddressResponse,
  EmployeePayMethodResponse,
  EmployeePayScheduleResponse,
  SaveEmployeePayScheduleInput,
  SaveEmployeePayMethodInput,
  AdminStateTaxResponse,
  FederalTaxElectionResponse,
  StateIncomeTaxFieldsResponse,
  ReciprocityRequestAdminItem,
  ReviewReciprocityInput,
  RevokeReciprocityInput,
  SaveFederalTaxElectionInput,
  ReviewStateWithholdingElectionInput,
  StateWithholdingCatalogResponse,
  StateWithholdingElectionsResponse,
  StateWithholdingSubmissionDetail,
  SubmitStateWithholdingElectionInput,
  EmployeeListPage,
  EmployeeWorkLocationResponse,
  EmployeeWorkLocationAcknowledgementResponse,
  JobTitleOption,
  SaveEmployeeAddressInput,
  SaveEmployeeWorkLocationInput,
  EmployeeResponse,
  ListContractorsParams,
  ListEmployeesParams,
  UpdateContractorInput,
  UpdateEmployeeInput,
} from './types';
import { mapTaxSetupExceptions, type TaxSetupExceptionQuery, type TaxSetupExceptions } from '../tax-settings/exceptions';
import type { TaxSetupStatus } from '../tax-settings/types';
import { mapTaxSetupStatus } from '../tax-settings/wire';

export class HttpPeopleGateway implements PeopleGateway {
  private readonly request = createJsonClient(employeesApiBaseUrl);
  // Work locations are owned by Identity (same-origin, no service prefix), like the Business Profile Setup gateway.
  private readonly identityRequest = createJsonClient('');
  // EPN-245: the tax engine sandbox fields are composed by Payroll Calculation; the location look-up is Payroll Configuration's existing endpoint.
  private readonly payrollCalculationRequest = createJsonClient(payrollCalculationApiBaseUrl);
  private readonly payrollConfigRequest = createJsonClient(payrollConfigApiBaseUrl);

  listEmployees(params?: ListEmployeesParams, signal?: AbortSignal): Promise<EmployeeListPage> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.status !== undefined) query.set('status', String(params.status));
    if (params?.departmentId) query.set('departmentId', params.departmentId);
    if (params?.jobTitleId) query.set('jobTitleId', params.jobTitleId);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortDescending) query.set('sortDescending', 'true');
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));

    const qs = query.toString();
    return this.request(`/api/employees${qs ? `?${qs}` : ''}`, undefined, signal);
  }

  getEmployee(id: string, signal?: AbortSignal): Promise<EmployeeResponse> {
    return this.request(`/api/employees/${id}`, undefined, signal);
  }

  createEmployee(input: CreateEmployeeInput): Promise<EmployeeResponse> {
    return this.request('/api/employees', { method: 'POST', body: JSON.stringify(input) });
  }

  updateEmployee(id: string, input: UpdateEmployeeInput): Promise<EmployeeResponse> {
    return this.request(`/api/employees/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }

  changeEmployeeStatus(id: string, status: number, terminationDate: string | null): Promise<EmployeeResponse> {
    return this.request(`/api/employees/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, terminationDate }),
    });
  }

  listDepartments(): Promise<DepartmentOption[]> {
    return this.request('/api/departments');
  }

  listJobTitles(): Promise<JobTitleOption[]> {
    return this.request('/api/job-titles');
  }

  createDepartment(name: string): Promise<DepartmentOption> {
    return this.request('/api/departments', { method: 'POST', body: JSON.stringify({ name, description: null }) });
  }

  /** The strict create of the Job Role wizard: `rejectDuplicates: true` turns off the legacy get-or-create-by-name behavior. */
  createDepartmentStrict(input: CreateDepartmentInput): Promise<DepartmentOption> {
    return this.request('/api/departments', {
      method: 'POST',
      body: JSON.stringify({ name: input.name, code: input.code, description: input.description, rejectDuplicates: true }),
    });
  }

  suggestDepartmentCode(name: string, signal?: AbortSignal): Promise<DepartmentCodeSuggestion> {
    return this.request(`/api/departments/suggest-code?name=${encodeURIComponent(name)}`, undefined, signal);
  }

  createJobTitle(name: string, departmentId: string | null): Promise<JobTitleOption> {
    return this.request('/api/job-titles', { method: 'POST', body: JSON.stringify({ name, description: null, departmentId }) });
  }

  listCompanyWorkLocations(): Promise<CompanyWorkLocationOption[]> {
    return this.identityRequest('/api/work-locations');
  }

  createEmployeeInvitation(employeeId: string, idempotencyKey: string): Promise<EmployeeInvitationState> {
    return this.identityRequest('/api/employee-invitations', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ employeeId }),
    });
  }

  async listEmployeeQuestions(status: 'open' | 'answered' | 'all'): Promise<EmployeeQuestionItem[]> {
    const result = await this.identityRequest<{ items: EmployeeQuestionItem[] }>(`/api/employee-questions?status=${status}`);
    return result.items;
  }

  replyToEmployeeQuestion(id: string, reply: string): Promise<EmployeeQuestionItem> {
    return this.identityRequest(`/api/employee-questions/${id}/reply`, { method: 'POST', body: JSON.stringify({ reply }) });
  }

  getEmployeeWorkLocation(employeeId: string): Promise<EmployeeWorkLocationResponse> {
    return this.request(`/api/employees/${employeeId}/work-location`);
  }

  saveEmployeeWorkLocation(employeeId: string, input: SaveEmployeeWorkLocationInput): Promise<EmployeeWorkLocationResponse> {
    return this.request(`/api/employees/${employeeId}/work-location`, { method: 'PUT', body: JSON.stringify(input) });
  }

  getEmployeeWorkLocationAcknowledgement(employeeId: string): Promise<EmployeeWorkLocationAcknowledgementResponse> {
    return this.request(`/api/employees/${employeeId}/work-location-acknowledgement`);
  }

  getEmployeeAddress(employeeId: string): Promise<EmployeeAddressResponse> {
    return this.request(`/api/employees/${employeeId}/address`);
  }

  saveEmployeeAddress(employeeId: string, input: SaveEmployeeAddressInput): Promise<EmployeeAddressResponse> {
    return this.request(`/api/employees/${employeeId}/address`, {
      method: 'PUT',
      body: JSON.stringify({ home: input.home, effectiveDate: input.effectiveDate, mailing: input.mailing }),
    });
  }

  getEmployeePayMethod(employeeId: string): Promise<EmployeePayMethodResponse> {
    return this.request(`/api/employees/${employeeId}/pay-method`);
  }

  getFederalTaxElection(employeeId: string): Promise<FederalTaxElectionResponse> {
    return this.request(`/api/employees/${employeeId}/federal-tax-election`);
  }

  saveFederalTaxElection(employeeId: string, input: SaveFederalTaxElectionInput): Promise<FederalTaxElectionResponse> {
    return this.request(`/api/employees/${employeeId}/federal-tax-election`, { method: 'PUT', body: JSON.stringify(input) });
  }

  getAdminStateTax(employeeId: string): Promise<AdminStateTaxResponse> {
    return this.request(`/api/employees/${employeeId}/state-tax`);
  }

  getStateIncomeTaxFields(employeeId: string): Promise<StateIncomeTaxFieldsResponse> {
    return this.payrollCalculationRequest(`/api/tax-calculation/employees/${employeeId}/state-income-tax-fields`);
  }

  async lookUpWorkLocationTaxJurisdictions(workLocationId: string): Promise<void> {
    await this.payrollConfigRequest(`/api/tax-payment-settings/location-profiles/${workLocationId}/resolve`, { method: 'POST' });
  }

  getStateWithholdingCatalog(): Promise<StateWithholdingCatalogResponse> {
    return this.request('/api/state-withholding-certificate-catalog');
  }

  getStateWithholdingElections(employeeId: string): Promise<StateWithholdingElectionsResponse> {
    return this.request(`/api/employees/${employeeId}/state-withholding-elections`);
  }

  submitStateWithholdingElection(employeeId: string, input: SubmitStateWithholdingElectionInput): Promise<StateWithholdingSubmissionDetail> {
    return this.request(`/api/employees/${employeeId}/state-withholding-elections`, { method: 'POST', body: JSON.stringify(input) });
  }

  reviewStateWithholdingElection(employeeId: string, submissionId: string, input: ReviewStateWithholdingElectionInput): Promise<StateWithholdingSubmissionDetail> {
    const body: ReviewStateWithholdingElectionInput = {
      decision: input.decision,
      ...(input.note ? { note: input.note } : {}),
      // An expiry belongs to an approval only.
      ...(input.decision === 'Approve' && input.expiresOn ? { expiresOn: input.expiresOn } : {}),
      concurrencyVersion: input.concurrencyVersion,
    };
    return this.request(`/api/employees/${employeeId}/state-withholding-elections/${submissionId}/review`, { method: 'PUT', body: JSON.stringify(body) });
  }

  withdrawStateWithholdingElection(employeeId: string, submissionId: string): Promise<StateWithholdingSubmissionDetail> {
    return this.request(`/api/employees/${employeeId}/state-withholding-elections/${submissionId}/withdraw`, { method: 'POST' });
  }

  listReciprocityRequests(employeeId: string): Promise<ReciprocityRequestAdminItem[]> {
    return this.request(`/api/employees/${employeeId}/reciprocity-requests`);
  }

  reviewReciprocityRequest(employeeId: string, requestId: string, input: ReviewReciprocityInput): Promise<ReciprocityRequestAdminItem> {
    const body: ReviewReciprocityInput = {
      decision: input.decision,
      ...(input.effectiveDate ? { effectiveDate: input.effectiveDate } : {}),
      // An expiry belongs to an approval only.
      ...(input.decision === 'Approve' && input.expiresOn ? { expiresOn: input.expiresOn } : {}),
      ...(input.note ? { note: input.note } : {}),
    };
    return this.request(`/api/employees/${employeeId}/reciprocity-requests/${requestId}/review`, { method: 'PUT', body: JSON.stringify(body) });
  }

  async revokeReciprocityRequest(employeeId: string, requestId: string, input: RevokeReciprocityInput): Promise<void> {
    await this.request(`/api/employees/${employeeId}/reciprocity-requests/${requestId}/revoke`, { method: 'POST', body: JSON.stringify({ note: input.note }) });
  }

  async getEmployeeTaxSetup(employeeId: string): Promise<TaxSetupStatus> {
    const status = mapTaxSetupStatus(await this.request<unknown>(`/api/employees/${employeeId}/tax-setup`));
    if (!status) throw new Error('The tax setup status could not be read.');
    return status;
  }

  async listTaxSetupExceptions(query?: TaxSetupExceptionQuery): Promise<TaxSetupExceptions> {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.pageSize) params.set('pageSize', String(query.pageSize));
    if (query?.reason) params.set('reason', query.reason);
    const qs = params.toString();
    const report = mapTaxSetupExceptions(await this.request<unknown>(`/api/employees/tax-setup-exceptions${qs ? `?${qs}` : ''}`));
    if (!report) throw new Error('The tax setup exception report could not be read.');
    return report;
  }

  reciprocityCertificateUrl(employeeId: string, requestId: string): string {
    return `${employeesApiBaseUrl}/api/employees/${employeeId}/reciprocity-requests/${requestId}/certificate`;
  }

  saveEmployeePayMethod(employeeId: string, input: SaveEmployeePayMethodInput): Promise<EmployeePayMethodResponse> {
    return this.request(`/api/employees/${employeeId}/pay-method`, {
      method: 'PUT',
      body: JSON.stringify({ method: input.method, effectiveDate: input.effectiveDate }),
    });
  }

  getEmployeePaySchedule(employeeId: string): Promise<EmployeePayScheduleResponse> {
    return this.request(`/api/employees/${employeeId}/pay-schedule`);
  }

  saveEmployeePaySchedule(employeeId: string, input: SaveEmployeePayScheduleInput): Promise<EmployeePayScheduleResponse> {
    return this.request(`/api/employees/${employeeId}/pay-schedule`, {
      method: 'PUT',
      body: JSON.stringify({ payScheduleId: input.payScheduleId, effectiveDate: input.effectiveDate }),
    });
  }

  listContractors(params?: ListContractorsParams, signal?: AbortSignal): Promise<ContractorListPage> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));

    const qs = query.toString();
    return this.request(`/api/contractors${qs ? `?${qs}` : ''}`, undefined, signal);
  }

  getContractor(id: string, signal?: AbortSignal): Promise<ContractorResponse> {
    return this.request(`/api/contractors/${id}`, undefined, signal);
  }

  createContractor(input: CreateContractorInput): Promise<ContractorResponse> {
    return this.request('/api/contractors', { method: 'POST', body: JSON.stringify(input) });
  }

  updateContractor(id: string, input: UpdateContractorInput): Promise<ContractorResponse> {
    return this.request(`/api/contractors/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }
}
